import React from 'react';
import { DamageLocation, DamageLocationLabels } from '../../redux/reducers/damageLocationsSlice';
import './VehicleDiagram.css';

interface VehicleDiagramProps {
  selectedLocations: DamageLocation[];
  onLocationClick: (location: DamageLocation) => void;
  disabled?: boolean;
}

// SVG path data for each damage zone
const damageZones = [
  {
    id: DamageLocation.FrontBumper,
    path: 'M 150 250 L 250 250 L 250 280 L 150 280 Z',
    label: 'Front Bumper'
  },
  {
    id: DamageLocation.Hood,
    path: 'M 150 180 L 250 180 L 250 250 L 150 250 Z',
    label: 'Hood'
  },
  {
    id: DamageLocation.FrontWindshield,
    path: 'M 150 140 L 250 140 L 250 180 L 150 180 Z',
    label: 'Windshield'
  },
  {
    id: DamageLocation.Roof,
    path: 'M 150 80 L 250 80 L 250 140 L 150 140 Z',
    label: 'Roof'
  },
  {
    id: DamageLocation.RearWindshield,
    path: 'M 150 40 L 250 40 L 250 80 L 150 80 Z',
    label: 'Rear Window'
  },
  {
    id: DamageLocation.TrunkTailgate,
    path: 'M 150 0 L 250 0 L 250 40 L 150 40 Z',
    label: 'Trunk'
  },
  {
    id: DamageLocation.RearBumper,
    path: 'M 150 -30 L 250 -30 L 250 0 L 150 0 Z',
    label: 'Rear Bumper'
  },
  {
    id: DamageLocation.LeftFrontDoor,
    path: 'M 100 160 L 150 160 L 150 220 L 100 220 Z',
    label: 'L Front Door'
  },
  {
    id: DamageLocation.LeftRearDoor,
    path: 'M 100 80 L 150 80 L 150 140 L 100 140 Z',
    label: 'L Rear Door'
  },
  {
    id: DamageLocation.LeftSide,
    path: 'M 50 60 L 100 60 L 100 240 L 50 240 Z',
    label: 'Left Side'
  },
  {
    id: DamageLocation.RightFrontDoor,
    path: 'M 250 160 L 300 160 L 300 220 L 250 220 Z',
    label: 'R Front Door'
  },
  {
    id: DamageLocation.RightRearDoor,
    path: 'M 250 80 L 300 80 L 300 140 L 250 140 Z',
    label: 'R Rear Door'
  },
  {
    id: DamageLocation.RightSide,
    path: 'M 300 60 L 350 60 L 350 240 L 300 240 Z',
    label: 'Right Side'
  }
];

const VehicleDiagram: React.FC<VehicleDiagramProps> = ({
  selectedLocations,
  onLocationClick,
  disabled = false
}) => {
  const isSelected = (location: DamageLocation) => {
    return selectedLocations.includes(location);
  };

  return (
    <div className="vehicle-diagram-container">
      <svg
        viewBox="-50 -50 450 350"
        className="vehicle-diagram"
        width="100%"
        height="100%"
      >
        {/* Vehicle body outline */}
        <g className="vehicle-body">
          {/* Main body */}
          <rect x="100" y="0" width="150" height="280" rx="10" fill="#e0e0e0" stroke="#666" strokeWidth="2" />
          
          {/* Hood curves */}
          <path d="M 100 180 Q 100 250, 150 250 L 250 250 Q 300 250, 300 180" 
                fill="none" stroke="#666" strokeWidth="2" />
          
          {/* Roof curves */}
          <path d="M 150 80 Q 200 60, 250 80" 
                fill="none" stroke="#666" strokeWidth="2" />
          
          {/* Wheels */}
          <circle cx="120" cy="260" r="20" fill="#333" />
          <circle cx="230" cy="260" r="20" fill="#333" />
          <circle cx="120" cy="20" r="20" fill="#333" />
          <circle cx="230" cy="20" r="20" fill="#333" />
          
          {/* Windows */}
          <rect x="120" y="100" width="110" height="60" rx="5" fill="#a0c4ff" fillOpacity="0.3" stroke="#666" />
          <line x1="175" y1="100" x2="175" y2="160" stroke="#666" strokeWidth="1" />
        </g>

        {/* Damage zones */}
        <g className="damage-zones">
          {damageZones.map((zone) => (
            <g key={zone.id}>
              <path
                d={zone.path}
                className={`damage-zone ${isSelected(zone.id) ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && onLocationClick(zone.id)}
                fill={isSelected(zone.id) ? 'rgba(255, 153, 2, 0.3)' : 'transparent'}
                stroke={isSelected(zone.id) ? '#FF9902' : 'transparent'}
                strokeWidth="3"
                style={{ cursor: disabled ? 'default' : 'pointer' }}
              />
              {isSelected(zone.id) && (
                <text
                  x={parseInt(zone.path.match(/M (\d+)/)?.[1] || '0') + 50}
                  y={parseInt(zone.path.match(/M \d+ (\d+)/)?.[1] || '0') + 20}
                  className="damage-label"
                  textAnchor="middle"
                  fill="#FF9902"
                  fontSize="12"
                  fontWeight="bold"
                >
                  {zone.label}
                </text>
              )}
            </g>
          ))}
        </g>

        {/* Legend */}
        <g className="legend" transform="translate(10, 300)">
          <text x="0" y="0" fontSize="12" fill="#666">Click areas to mark damage</text>
        </g>
      </svg>

      {/* Selected locations summary */}
      <div className="selected-locations">
        {selectedLocations.length > 0 ? (
          <>
            <h4>Selected Damage Areas ({selectedLocations.length}):</h4>
            <div className="location-tags">
              {selectedLocations.map(location => (
                <span key={location} className="location-tag">
                  {DamageLocationLabels[location]}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="no-selection">No damage areas selected</p>
        )}
      </div>
    </div>
  );
};

export default VehicleDiagram;